import { z } from 'zod';

import { MAX_SITE_DATABASES, siteDatabaseSpecSchema } from '../access/database';
import { MAX_SITE_SFTP_ACCOUNTS, siteSftpSpecSchema } from '../access/sftp';
import { belongsToSite } from '../access/site-prefix';
import {
  hostnameSchema,
  portSchema,
  positiveIntSchema,
  slugSchema,
  uuidSchema,
} from '../common/primitives';

import { blueprintReferenceSchema } from './blueprint';
import { postInstallSchema } from './post-install';

/**
 * Die Spezifikation einer Site — das Einzige, was der Master dem Agent über
 * eine Website mitteilt.
 *
 * ## Der Leitsatz, aus dem dieses Schema besteht
 *
 * Der Agent nimmt **Spezifikationen** entgegen, niemals **Befehle**. Er hat
 * Zugriff auf den Docker-Socket, und der ist gleichbedeutend mit Root auf dem
 * Server. Ein kompromittierter oder fehlerhafter Master darf daraus keinen
 * Serverzugriff machen können.
 *
 * Operativ heißt das: **Was hier nicht steht, kann nicht angefordert werden.**
 * Der Agent baut seine Docker-Nutzlast Feld für Feld aus diesem Schema
 * zusammen und breitet niemals ein unbekanntes Objekt hinein aus.
 *
 * Bewusst **nicht** vorhanden und auch nicht nachträglich zu ergänzen, ohne den
 * Entwurf neu zu prüfen: `command`, `entrypoint`, `user`, `privileged`,
 * `capAdd`, `devices`, `networkMode`, `pidMode`, Host-Pfade in Einhängungen,
 * `extraHosts`, `sysctls`, freie Docker-Labels, `restartPolicy` (setzt der
 * Agent) und jede Form von Host-Verzeichnis. Der Master kennt keinen Pfad auf
 * dem Worker und braucht auch keinen.
 *
 * Seit M5 gibt es mit `services[].action` eine Einrichtungshandlung — und sie
 * ist ausdrücklich **kein** `command`: Übertragen wird ein Wert aus einer
 * geschlossenen Aufzählung, die Argumentliste bildet der Agent selbst. Die
 * ausführliche Begründung steht in `post-install.ts`.
 *
 * ## Seit M4a trägt die Spezifikation Geheimnisse
 *
 * `databases[].password` steht hier im Klartext. Das ist keine Nachlässigkeit,
 * sondern die Folge des Leitsatzes: Die Spezifikation ist alles, was der Agent
 * braucht — er stellt keine Rückfragen und holt sich nichts nach. Getragen wird
 * das an drei Stellen: `site.spec` liegt beim Master verschlüsselt, die Queue
 * transportiert nur eine Operations-ID, und die Log-Redaction schwärzt das Feld
 * `spec` als Ganzes. Auf dem Worker ist das Passwort unvermeidlich im Klartext —
 * es steht in der Umgebung des Containers.
 */

// -- Bausteine ---------------------------------------------------------------

/** Ein Namensabschnitt eines Image-Pfads, z. B. `library` oder `nginx`. */
const IMAGE_SEGMENT_PATTERN = /^[a-z\d]+(?:[._-][a-z\d]+)*$/;
/** Registry-Adresse mit optionalem Port, z. B. `registry.example.com:5000`. */
const IMAGE_REGISTRY_PATTERN = /^[a-z\d][a-z\d.-]*(?::\d+)?$/;
/** Tag, z. B. `1.27-alpine`. */
const IMAGE_TAG_PATTERN = /^\w[\w.-]{0,127}$/;
/** Digest, z. B. `sha256:0123…`. */
const IMAGE_DIGEST_PATTERN = /^sha256:[a-f\d]{64}$/;

/**
 * Trennt Tag oder Digest ab und gibt den reinen Namensteil zurück.
 * `null`, wenn beides fehlt oder nicht dem erlaubten Zeichenvorrat entspricht.
 */
function stripImageVersion(value: string): string | null {
  const digestSeparator = value.indexOf('@');

  if (digestSeparator !== -1) {
    return IMAGE_DIGEST_PATTERN.test(value.slice(digestSeparator + 1))
      ? value.slice(0, digestSeparator)
      : null;
  }

  const lastColon = value.lastIndexOf(':');

  // Ein Doppelpunkt vor dem letzten Schrägstrich gehört zum Registry-Port und
  // nicht zum Tag. Genau hier scheitern selbstgebaute Prüfungen üblicherweise:
  // "registry.example.com:5000/app" gälte dann als getaggt.
  if (lastColon <= value.lastIndexOf('/')) {
    return null;
  }

  return IMAGE_TAG_PATTERN.test(value.slice(lastColon + 1)) ? value.slice(0, lastColon) : null;
}

/**
 * Zerlegt und prüft eine Image-Referenz.
 *
 * Bewusst eine Funktion aus kleinen, je für sich lesbaren Ausdrücken statt
 * eines einzigen großen. Der zusammengesetzte Ausdruck war zwar korrekt, aber
 * niemand hätte ihn beim nächsten Mal noch geprüft — und dieser eine Ausdruck
 * entscheidet, welche Images auf einem Server mit Root-äquivalentem
 * Docker-Zugriff gestartet werden dürfen.
 */
export function isValidImageReference(value: string): boolean {
  if (value.length === 0 || value.length > 255) {
    return false;
  }

  const name = stripImageVersion(value);

  if (name === null) {
    return false;
  }

  const parts = name.split('/');
  const first = parts[0];

  if (first === undefined) {
    return false;
  }

  // Der erste Abschnitt ist die Registry, wenn er wie ein Hostname aussieht.
  if (parts.length > 1 && (first.includes('.') || first.includes(':') || first === 'localhost')) {
    if (!IMAGE_REGISTRY_PATTERN.test(first)) {
      return false;
    }

    parts.shift();
  }

  return parts.length > 0 && parts.every((part) => IMAGE_SEGMENT_PATTERN.test(part));
}

/**
 * Docker-Image mit **zwingendem** Tag oder Digest.
 *
 * Ein implizites `latest` wäre bei einem Rollout nicht reproduzierbar und würde
 * jedes erneute Anwenden zu einem stillen Upgrade machen — auf einer Website,
 * die ein Kunde bezahlt hat.
 */
export const imageReferenceSchema = z.string().refine(isValidImageReference, {
  message:
    'Muss ein Docker-Image mit Tag oder Digest sein, z. B. "nginx:1.27-alpine" oder "nginx@sha256:…". Ein Image ohne Tag ist nicht reproduzierbar.',
});

/**
 * Relativer Unterpfad innerhalb des Site-Verzeichnisses, z. B. `public`.
 *
 * Der Master gibt ausdrücklich **keinen** Host-Pfad an. Der Agent setzt den
 * absoluten Pfad unter seinem eigenen Datenverzeichnis zusammen und prüft das
 * Ergebnis danach ein zweites Mal — erst diese zweite Prüfung ist gegen
 * Normalisierung und Unicode wirklich dicht.
 *
 * Ein Segment muss mit einem Buchstaben oder einer Ziffer beginnen; damit sind
 * `..` und versteckte Dateien strukturell ausgeschlossen, nicht bloß gefiltert.
 */
export const relativeSubpathSchema = z
  .string()
  .min(1, { message: 'Pfad darf nicht leer sein.' })
  .max(255, { message: 'Pfad ist zu lang.' })
  .regex(/^[a-z\d][a-z\d._-]*(?:\/[a-z\d][a-z\d._-]*)*$/, {
    message:
      'Muss ein relativer Unterpfad aus Kleinbuchstaben, Ziffern, Punkt, Unterstrich und Bindestrich sein — ohne führenden Schrägstrich und ohne "..".',
  });

/** Absoluter Pfad **innerhalb** des Containers, z. B. `/usr/share/nginx/html`. */
export const absoluteContainerPathSchema = z
  .string()
  .max(512, { message: 'Pfad ist zu lang.' })
  .regex(/^(?:\/[a-zA-Z\d][\w.-]*)+$/, {
    message: 'Muss ein absoluter Pfad im Container sein, z. B. "/usr/share/nginx/html".',
  });

/** Name einer Umgebungsvariablen nach POSIX-Konvention. */
export const envKeySchema = z.string().regex(/^[A-Z][A-Z\d_]{0,63}$/, {
  message:
    'Muss aus Großbuchstaben, Ziffern und Unterstrichen bestehen und mit einem Buchstaben beginnen.',
});

/** Name eines Docker-Objekts (Netz, Container). */
export const dockerNameSchema = z.string().regex(/^[a-zA-Z\d][\w.-]{0,127}$/, {
  message: 'Muss ein gültiger Docker-Name sein.',
});

/**
 * Die Docker-Labels, die der Agent an jeden von ihm erzeugten Container hängt.
 *
 * Sie sind die **Identität** eines Containers — nicht sein Name. Ein Absturz
 * zwischen `create` und dem Notieren hinterlässt sonst einen Container, den
 * niemand mehr zuordnen kann; über das Label findet ihn der nächste Durchlauf.
 */
export const SITE_LABELS = {
  managedBy: 'kraaapf.managed-by',
  siteId: 'kraaapf.site-id',
  service: 'kraaapf.service',
  specHash: 'kraaapf.spec-hash',
  operationId: 'kraaapf.operation-id',
  /**
   * Die XFS-Projektnummer dieser Site. Fehlt, solange keine Quote gilt.
   *
   * Sie steht hier, weil der Agent im Heartbeat die Belegung je Site melden
   * soll: `xfs_quota report -p` liefert Zahlen je **Projekt**, und der Agent
   * braucht die Zuordnung Projekt → Site. Ohne dieses Label müsste er dafür
   * einen eigenen Zustandsspeicher führen — und der wäre nach einem Neustart
   * genau dann leer, wenn er gebraucht wird.
   *
   * Ausdrücklich **nicht** Teil von `matchesPlan`: Ein Label ist keine
   * Eigenschaft, an der ein Container ausgetauscht werden müsste.
   */
  quotaProjectId: 'kraaapf.quota-project',
} as const;

/** Wert von `kraaapf.managed-by` — Erkennungsmerkmal für alles, was uns gehört. */
export const MANAGED_BY_VALUE = 'kraaapf-agent';

// -- Dienst innerhalb einer Site ---------------------------------------------

/** Eine Einhängung: ein Unterverzeichnis der Site in einen Container-Pfad. */
export const siteMountSchema = z.object({
  /** Relativ zum Site-Verzeichnis des Agents. */
  source: relativeSubpathSchema,
  target: absoluteContainerPathSchema,
  readOnly: z.boolean().default(false),
});

/**
 * Harte Ressourcengrenzen. **Pflicht, nicht optional.**
 *
 * Fehlende Grenzen sind der klassische Ausfall durch einen lauten Nachbarn:
 * Eine Website zieht den ganzen Server herunter, und alle anderen Kunden merken
 * es zuerst. Ein Vorgabewert wäre hier schlimmer als eine Pflichtangabe — er
 * würde nie hinterfragt.
 */
export const siteResourcesSchema = z.object({
  /** 1000 = ein voller Kern. */
  cpuMilli: positiveIntSchema.max(64_000),
  memoryMb: positiveIntSchema.max(131_072),
  /** Obergrenze der Prozesse — bremst Fork-Bomben, bevor der Host reagieren muss. */
  pidsLimit: positiveIntSchema.max(4096).default(512),
});

/**
 * Absicherung des Containers.
 *
 * Als Objekt mit festgeschriebenen Werten statt als Vorgabe im Agent: So steht
 * in jeder übertragenen Spezifikation nachweisbar, dass sie gilt, und ein
 * Weglassen scheitert an der Validierung statt still zu einer laxeren
 * Konfiguration zu führen.
 */
/**
 * Die einzigen Capabilities, die ein Blueprint zurückfordern darf.
 *
 * `capDrop: ALL` allein ist zu streng für gängige Images: Das offizielle
 * NGINX-Image ruft beim Start `chown` auf sein Cache-Verzeichnis auf und
 * wechselt danach auf einen unprivilegierten Benutzer — ohne `CHOWN`, `SETUID`
 * und `SETGID` bricht es mit `Operation not permitted` ab und landet in einer
 * Neustartschleife. Genau das ist beim Abnahmetest von M2 passiert.
 *
 * Die Antwort darauf ist **nicht**, weniger zu entziehen, sondern gezielt
 * zurückzugeben: Erst alles fallen lassen, dann eine feste, hier aufgezählte
 * Auswahl wieder erlauben. Ein freies Feld für Capabilities hätte diese Grenze
 * aufgehoben — `SYS_ADMIN` steht bewusst nicht in dieser Liste und wird auch
 * nie hineingehören.
 */
export const GRANTABLE_CAPABILITIES = [
  /** Eigentümer einer Datei ändern — von vielen Images beim Start gebraucht. */
  'CHOWN',
  /** Auf einen unprivilegierten Benutzer wechseln. */
  'SETUID',
  'SETGID',
  /** Dateirechte übergehen; einige Images legen damit ihre Laufzeitdateien an. */
  'DAC_OVERRIDE',
  /** Eigentümerprüfung übergehen. */
  'FOWNER',
  /** Auf einen Port unter 1024 lauschen. */
  'NET_BIND_SERVICE',
] as const;

export const siteSecuritySchema = z.object({
  noNewPrivileges: z.literal(true),
  capDrop: z.literal('ALL'),
  /**
   * Nach dem Entzug gezielt zurückgegebene Capabilities.
   * Ausschließlich Werte aus `GRANTABLE_CAPABILITIES` — die Aufzählung ist die
   * Grenze, nicht eine Prüfung, die man vergessen kann.
   */
  capAdd: z.array(z.enum(GRANTABLE_CAPABILITIES)).max(6).default([]),
  readOnlyRootFs: z.boolean().default(false),
});

/**
 * Wofür ein Dienst da ist.
 *
 * `app` läuft dauerhaft und wird vom Agent überwacht. `init` läuft **einmal**
 * nach dem Start des Stacks, richtet die Anwendung ein und beendet sich; er
 * bekommt keine Neustartregel, keine Traefik-Route und zählt weder in die
 * Kapazitätsreservierung noch in die Zählung „genau ein Dienst mit `http`".
 *
 * Fehlt das Feld, gilt `app`. Das ist kein Vorgabewert im Schema, sondern eine
 * Auslegung im Code — ein Vorgabewert stünde in der kanonischen Form und
 * änderte damit den Fingerabdruck jeder Spezifikation der Version 3.
 */
export const SERVICE_ROLES = { app: 'app', init: 'init' } as const;
export type ServiceRole = (typeof SERVICE_ROLES)[keyof typeof SERVICE_ROLES];

export const siteServiceSpecSchema = z.object({
  name: slugSchema,
  image: imageReferenceSchema,
  /** Siehe `SERVICE_ROLES`. Fehlt der Wert, ist der Dienst ein `app`-Dienst. */
  role: z.enum([SERVICE_ROLES.app, SERVICE_ROLES.init]).optional(),
  /**
   * Die Einrichtungshandlung eines `init`-Dienstes.
   *
   * Ein Wert aus einer geschlossenen Aufzählung — **keine** Kommandozeile. Was
   * daraus wird, entscheidet allein der Agent (`post-install.ts`).
   */
  action: postInstallSchema.optional(),
  env: z
    .array(z.object({ key: envKeySchema, value: z.string().max(4096) }))
    .max(64)
    .default([]),
  mounts: z.array(siteMountSchema).max(16).default([]),
  /**
   * Ist dieser Wert gesetzt, bekommt der Dienst die Traefik-Route.
   * Genau **ein** Dienst je Site darf das — sonst wäre unklar, wohin der
   * Host-Header zeigt.
   */
  http: z.object({ port: portSchema }).nullable().default(null),
  resources: siteResourcesSchema,
  security: siteSecuritySchema,
  dependsOn: z.array(slugSchema).max(8).default([]),
});

export type SiteServiceSpec = z.infer<typeof siteServiceSpecSchema>;

// -- Die Spezifikation selbst ------------------------------------------------

/**
 * Obergrenze der Host-Header je Site.
 *
 * Zehn reichen für einen Apex, sein `www` und ein paar umgeleitete Altdomains.
 * Die Grenze steht hier, weil aus jedem Eintrag ein Term in Traefiks
 * Routing-Regel wird: Ohne sie ließe sich über eine einzige Site eine beliebig
 * lange Regel erzeugen.
 */
export const MAX_SITE_HOSTNAMES = 10;

/**
 * Version des Spezifikationsformats.
 *
 * **1 → 2 mit M4a:** `databases` und `network.dataNetwork` kommen hinzu. Der
 * Bruch ist ausdrücklich gewollt statt eines optionalen Feldes: Ein Agent im
 * Stand vor M4a würde eine Spezifikation mit Datenbanken zwar annehmen, aber die
 * Datenbank nie anlegen — die Site liefe an, und der Kunde bekäme einen
 * Verbindungsfehler statt einer Fehlermeldung. Als harte Version scheitert
 * dasselbe sofort und sichtbar mit `SITE_SPEC_REJECTED`.
 *
 * **2 → 3 mit M4c:** `sftpAccounts` kommt hinzu, und der Bruch folgt derselben
 * Begründung: Ein Agent im Stand vor M4c nähme eine Spezifikation mit
 * SFTP-Konten an und legte die Konten nie an. Der Kunde bekäme kein
 * abgelehntes, sondern ein stumm nicht funktionierendes Konto — der schlechteste
 * aller Ausgänge. Als harte Version scheitert dasselbe sofort und sichtbar mit
 * `SITE_SPEC_REJECTED`.
 *
 * **3 → 4 mit M5:** `dataOwner` sowie `role` und `action` je Dienst kommen
 * hinzu. Ein Agent im Stand vor M5 nähme eine Spezifikation mit einer
 * Einrichtungshandlung an und richtete nichts ein — die Website liefe an und
 * zeigte dem ersten Besucher den Installationsassistenten, der ihn zum
 * Administrator macht. Wieder der schlechteste aller Ausgänge, wieder als harte
 * Version abgefangen.
 *
 * **4 → 5 mit M6b:** `quotaProjectId` und `quotaMb` kommen hinzu. Ein Agent im
 * Stand vor M6b nähme eine Spezifikation mit Quote an und setzte keine. Der
 * Kunde hätte eine Grenze, die niemand durchsetzt, und — schlimmer — der
 * Betreiber hielte den Server für geschützt und stellte die Bestellungen
 * entsprechend eng. Der Ausfall käme dann als voller Worker, nicht als
 * Fehlermeldung. Wieder als harte Version abgefangen.
 *
 * Folge für den Rollout: erst den Worker ausliefern, dann den Master. Siehe
 * `ACCEPTED_SITE_SPEC_VERSIONS` — genau dafür gibt es sie.
 */
export const SITE_SPEC_VERSION = 5;

/**
 * Die Version davor, die ein Agent **zusätzlich** annimmt.
 *
 * ## Warum das kein Bruch der Doktrin aus M4a und M4c ist
 *
 * Die harte Version schützt gegen genau eine Richtung: Ein Agent darf ein Feld,
 * das er nicht kennt, nicht **stillschweigend übergehen**. Eine ältere und
 * damit kleinere Spezifikation übergeht nichts — sie enthält die neuen Felder
 * gar nicht. Der Riegel bleibt vollständig erhalten: Ein Agent im Stand vor M6b
 * kennt `5` nicht und lehnt weiterhin ab.
 *
 * ## Was es einbringt
 *
 * Es nimmt dem Rollout sein Ausfallfenster. Eine Domain fällt aus der
 * Edge-Konfiguration, sobald Soll- und Ist-Fingerabdruck auseinanderlaufen. Ohne
 * diese Zeile müsste zuerst der Master ausgeliefert werden (er verteilt das
 * Agent-Artefakt), der alte Agent lehnte danach jede Spezifikation **dauerhaft**
 * ab (`SITE_SPEC_REJECTED` ist nicht wiederholbar) — und die Kundenseite bliebe
 * bis zum Agent-Update **plus einem Eingriff von Hand** auf 404. Mit ihr zieht
 * erst die Flotte nach (unverändertes Verhalten, keine Spezifikation ändert
 * sich), und der neue Master tauscht danach je Site einmal den Container.
 *
 * ## Wann diese Zeile wieder verschwindet
 *
 * Sobald keine Site mehr eine Spezifikation der Version 4 trägt. Das ist nach
 * dem ersten Start des neuen Masters der Fall — `SiteSpecService` hebt beim
 * Hochfahren jede überholte Spezifikation an. Die Zeile ist also
 * Übergangsgepäck und ausdrücklich mit einem Ablaufdatum versehen.
 */
export const PREVIOUS_SITE_SPEC_VERSION = 4;

export const ACCEPTED_SITE_SPEC_VERSIONS = [PREVIOUS_SITE_SPEC_VERSION, SITE_SPEC_VERSION] as const;

/**
 * Die Kennung, unter der die Dateien einer Site auf dem Worker liegen.
 *
 * ## Warum die Spezifikation das überhaupt trägt
 *
 * Bis M4c schrieb nur SFTPGo in das Verzeichnis einer Site, und die Kennung war
 * allein Sache des Agents. Seit M5 schreibt die Anwendung selbst — WordPress
 * legt Uploads, Cache und Erweiterungen an. Beide müssen dieselbe Kennung
 * benutzen; sonst gehören die Dateien einer Site abwechselnd zwei Benutzern und
 * der Kunde kommt an einen Teil davon nicht heran (ADR 0010 §5).
 *
 * Die Anwendung erfährt sie über eine Umgebungsvariable — bei WordPress
 * `APACHE_RUN_USER`. Deshalb muss der Wert **renderbar** sein und kann nicht
 * länger allein im Agent stehen.
 *
 * ## Warum das trotzdem keine Anweisung ist
 *
 * Der Agent prüft den Wert gegen seine eigene Konfiguration und lehnt jede
 * Abweichung ab — dieselbe Bauart wie bei Ingress-Netz, Einstiegspunkt und
 * Datennetz. Der Master kann hier also nicht *bestimmen*, unter welcher Kennung
 * etwas läuft; er kann nur *wiederholen*, was der Node ohnehin festgelegt hat.
 * Läuft beides auseinander, ist das ein Konfigurationsfehler mit einer klaren
 * Meldung und kein stiller Rechtefehler.
 */
export const siteDataOwnerSchema = z.object({
  uid: z.number().int().min(1).max(65_535),
  gid: z.number().int().min(1).max(65_535),
});

export type SiteDataOwner = z.infer<typeof siteDataOwnerSchema>;

/**
 * Die kleinste Projektnummer, die der Master vergibt.
 *
 * 0 bis 999 bleiben frei. 0 ist im Kernel die Nummer „kein Projekt" — alles,
 * was nicht verbucht wird, trägt sie; der Rest des Bereichs bleibt für die
 * Hand des Betreibers reserviert, falls auf einem Worker jemals ein zweites
 * Projekt neben den Sites entstehen sollte.
 */
export const MIN_QUOTA_PROJECT_ID = 1000;

/** Obergrenze der Projektnummer — `prid_t` ist im Kernel 32 Bit ohne Vorzeichen. */
export const MAX_QUOTA_PROJECT_ID = 4_294_967_294;

/**
 * Obergrenze der Quote. 4 TiB je Site ist keine Erwartung, sondern ein Riegel
 * gegen eine verrutschte Einheit: Wer versehentlich Bytes statt Mebibyte
 * einträgt, scheitert an der Validierung statt an einer Grenze, die nie greift.
 */
export const MAX_QUOTA_MB = 4_194_304;

export const siteSpecSchema = z
  .object({
    /**
     * Version des Spezifikationsformats.
     *
     * Angenommen wird die aktuelle **und** die vorherige; abgelehnt wird alles
     * darunter und alles darüber. Die Begründung dieser Asymmetrie steht bei
     * `PREVIOUS_SITE_SPEC_VERSION`.
     */
    specVersion: z.union([z.literal(PREVIOUS_SITE_SPEC_VERSION), z.literal(SITE_SPEC_VERSION)]),
    siteId: uuidSchema,
    /**
     * Kennung für die Dateien dieser Site. Fehlt bei Version 3.
     *
     * Ausdrücklich `.optional()` **ohne** Vorgabewert: `canonicalJson` lässt
     * `undefined` weg, und nur deshalb kommt der Agent bei einer
     * Spezifikation der Version 3 auf denselben Fingerabdruck wie der Master,
     * der sie geschrieben hat. Ein Vorgabewert — auch `null` — stünde in der
     * kanonischen Form und ließe die gesamte Flotte endlos neu anwenden.
     */
    dataOwner: siteDataOwnerSchema.optional(),
    /**
     * Die XFS-Projektnummer, unter der die Platten-Belegung dieser Site
     * verbucht wird. Fehlt bei Version 4 und auf einer Plattform ohne Quoten.
     *
     * ## Warum eine Nummer und kein Name
     *
     * Weil der Kernel Nummern führt. Sie kommt vom Master, weil sie über die
     * ganze Flotte hinweg eindeutig sein muss und weil eine aus der Site-ID
     * abgeleitete Nummer kollidieren könnte — 32 Bit gegen 128 Bit.
     *
     * ## Warum das trotzdem keine Anweisung ist
     *
     * Es ist eine **Zahl**, kein Pfad und kein Kommando. Der Agent bildet die
     * Argumentliste für `xfs_quota` selbst aus eigenen Konstanten und dem
     * Pfad, den nur er kennt (`site-paths.ts`). Dieselbe Bauart wie bei
     * `action`: Übertragen wird ein Wert, gebildet wird der Aufruf am Ziel.
     *
     * Ausdrücklich `.optional()` **ohne** Vorgabewert — die Begründung steht
     * bei `dataOwner` und gilt hier unverändert.
     */
    quotaProjectId: z.number().int().min(MIN_QUOTA_PROJECT_ID).max(MAX_QUOTA_PROJECT_ID).optional(),
    /**
     * Die harte Grenze dieser Site in Mebibyte — Dateien **und** Datenbank.
     *
     * Es ist `site.reservedResources.diskGb`, dieselbe Zahl, die der Scheduler
     * auf dem Node reserviert. Eine Zahl, zwei Wirkungen: Der Master platziert
     * nicht mehr, sobald die Summe den Vorrat erreicht, und der Kernel hält
     * jede einzelne Site in ihrem Anteil. Überbuchung ist damit strukturell
     * ausgeschlossen und nicht bloß unwahrscheinlich.
     *
     * Mebibyte und nicht Gibibyte, weil `xfs_quota` in dieser Körnung rechnet
     * und weil die Zahl ab M6c aus einem Paket kommt, das nicht auf ganze
     * Gibibyte fallen muss.
     */
    quotaMb: positiveIntSchema.max(MAX_QUOTA_MB).optional(),
    /**
     * Soll der Stack laufen oder nur existieren?
     *
     * Bewusst Teil der Spezifikation und kein eigenes Verb auf der Leitung:
     * "Stoppen" ist damit ein gewöhnliches Anwenden, und der Agent braucht
     * keinen zweiten idempotenten Codepfad, der eigens abgesichert und getestet
     * werden müsste.
     */
    runState: z.enum(['running', 'stopped']),
    blueprint: blueprintReferenceSchema,
    /**
     * Alle Host-Header, unter denen diese Site angenommen wird: der interne
     * Routing-Schlüssel und jede verifizierte Kundendomain.
     *
     * Das ist die einzige Stelle, an der Zeichenketten des Masters doch in einer
     * Ausdruckssprache landen — in Traefiks Regel
     * ``Host(`a`) || Host(`b`)``. Ein Backtick oder ein `||` in einem Eintrag
     * wäre eine Routing-Übernahme. `hostnameSchema` lässt nur Buchstaben,
     * Ziffern, Bindestrich und Punkt zu, und der Agent prüft jeden Eintrag vor
     * dem Bau der Regel ein zweites Mal.
     *
     * **Die Liste wird sortiert.** `canonicalJson` erhält Array-Reihenfolgen,
     * weil sie bei Diensten und Einhängungen bedeutungstragend sind. Hier ist
     * sie es nicht: Für die Routing-Regel zählt die Menge. Ohne Sortierung hinge
     * der Spec-Hash daran, in welcher Reihenfolge der Master seine Domains
     * zufällig gelesen hat — und eine harmlose Umstellung ließe die gesamte
     * Flotte neu anwenden.
     *
     * Welcher Name der kanonische ist (Apex statt `www`), steht bewusst **nicht**
     * hier: Der Agent braucht die Menge, nicht die Rangfolge. Die Umleitung von
     * `www` auf den Apex entscheidet die Edge, und der kanonische Name steckt
     * bereits in den gerenderten Umgebungsvariablen.
     */
    hostnames: z
      .array(hostnameSchema)
      .min(1, { message: 'Eine Site braucht mindestens einen Host-Header.' })
      .max(MAX_SITE_HOSTNAMES, {
        message: `Höchstens ${String(MAX_SITE_HOSTNAMES)} Host-Header je Site.`,
      })
      // Ausdrücklich ein Vergleich über die Zeichenwerte und **nicht** über
      // `localeCompare`: Dessen Ergebnis hängt von der Spracheinstellung ab,
      // und ein Fingerabdruck, der auf zwei Servern verschieden ausfällt, wäre
      // schlimmer als gar keine Sortierung.
      .transform((values) => [...values].sort((left, right) => (left < right ? -1 : 1))),
    network: z.object({
      /** Geteiltes Netz, in dem der lokale Traefik lauscht. */
      ingressNetwork: dockerNameSchema,
      /** Name des Traefik-Einstiegspunkts (`web`). */
      entrypoint: slugSchema,
      /**
       * Geteiltes Netz, in dem der MariaDB-Server des Workers lauscht.
       *
       * Der Agent hängt einen Site-Container **nur dann** zusätzlich hier hinein,
       * wenn `databases` nicht leer ist. Eine rein statische Seite sieht den
       * Datenbankserver damit gar nicht — sie kann ihn nicht einmal anpingen,
       * geschweige denn Anmeldeversuche gegen fremde Benutzer fahren.
       */
      dataNetwork: dockerNameSchema,
    }),
    services: z.array(siteServiceSpecSchema).min(1).max(8),
    /**
     * Die Kunden-Datenbanken dieser Site auf dem geteilten MariaDB-Server.
     *
     * **Die Liste wird sortiert**, aus demselben Grund wie `hostnames`: Für den
     * Abgleich zählt die Menge, nicht die Reihenfolge. Ohne Sortierung hinge der
     * Spec-Hash daran, in welcher Reihenfolge der Master seine Zeilen gelesen
     * hat, und eine geänderte Sortierung in einer Datenbankabfrage ließe die
     * gesamte Flotte neu anwenden.
     *
     * Leer ist der Normalfall: Eine Datenbank ist eine eigenständige Ressource,
     * die zu jeder Site bestellt werden kann — auch zu einer statischen Seite —
     * aber zu keiner gehören muss.
     */
    databases: z
      .array(siteDatabaseSpecSchema)
      .max(MAX_SITE_DATABASES, {
        message: `Höchstens ${String(MAX_SITE_DATABASES)} Datenbanken je Site.`,
      })
      .default([])
      .transform((values) => [...values].sort((left, right) => (left.name < right.name ? -1 : 1))),
    /**
     * Die SFTP-Konten dieser Site auf dem Dienst des Workers.
     *
     * **Die Liste wird sortiert**, aus demselben Grund wie `databases` und
     * `hostnames`: Für den Abgleich zählt die Menge, nicht die Reihenfolge.
     *
     * Anders als bei den Datenbanken ist leer **nicht** der Normalfall: Jede
     * Site bekommt beim Bestellen ein Konto, denn ohne einen Weg, Dateien
     * hineinzubekommen, ist eine Website kein Angebot. Leer bleibt möglich —
     * ein Kunde kann sein letztes Konto löschen lassen.
     */
    sftpAccounts: z
      .array(siteSftpSpecSchema)
      .max(MAX_SITE_SFTP_ACCOUNTS, {
        message: `Höchstens ${String(MAX_SITE_SFTP_ACCOUNTS)} SFTP-Konten je Site.`,
      })
      .default([])
      .transform((values) =>
        [...values].sort((left, right) => (left.username < right.username ? -1 : 1)),
      ),
  })
  .superRefine((spec, ctx) => {
    if (new Set(spec.hostnames).size !== spec.hostnames.length) {
      // Ein doppelter Eintrag erzeugte stillschweigend eine Regel mit
      // demselben Term zweimal. Traefik stört das nicht, aber es heißt, dass an
      // anderer Stelle etwas doppelt gezählt wurde — und der Spec-Hash hinge
      // dann daran, wie oft.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hostnames'],
        message: 'Host-Header müssen innerhalb einer Site eindeutig sein.',
      });
    }

    const httpServices = spec.services.filter((service) => service.http !== null);

    if (httpServices.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services'],
        message: `Genau ein Dienst muss "http" tragen — er bekommt die Traefik-Route. Gefunden: ${String(httpServices.length)}.`,
      });
    }

    checkInitServices(spec, ctx);

    const names = new Set(spec.services.map((service) => service.name));

    if (names.size !== spec.services.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services'],
        message: 'Dienstnamen müssen innerhalb einer Site eindeutig sein.',
      });
    }

    for (const service of spec.services) {
      for (const dependency of service.dependsOn) {
        if (!names.has(dependency)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['services'],
            message: `Dienst "${service.name}" hängt von "${dependency}" ab, das es in dieser Site nicht gibt.`,
          });
        }
      }
    }

    checkDatabases(spec, ctx);
    checkSftpAccounts(spec, ctx);
    checkQuota(spec, ctx);
  });

/**
 * Die Prüfungen rund um die Quote.
 *
 * Zwei Zusagen, auf die sich der Agent verlässt:
 *
 * 1. **Nummer und Grenze gehören zusammen.** Eine Nummer ohne Grenze wäre ein
 *    Projekt, das zählt und nichts begrenzt; eine Grenze ohne Nummer wäre eine
 *    Zahl ohne Adressat. Beides fiele erst auf dem Node auf, und zwar als
 *    Quote, die es nicht gibt.
 * 2. **Version 4 trägt keine Quote.** Sie kennt die Felder nicht. Stünden sie
 *    trotzdem darin, hätte jemand eine alte Spezifikation von Hand ergänzt —
 *    und der Fingerabdruck dieser Site liefe gegen den des Masters.
 */
function checkQuota(
  spec: { specVersion: number; quotaProjectId?: number; quotaMb?: number },
  ctx: z.RefinementCtx,
): void {
  const hasProject = spec.quotaProjectId !== undefined;
  const hasLimit = spec.quotaMb !== undefined;

  if (hasProject !== hasLimit) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['quotaMb'],
      message:
        'Projektnummer ("quotaProjectId") und Grenze ("quotaMb") kommen nur gemeinsam vor. Eine Nummer ohne Grenze zählt und begrenzt nichts; eine Grenze ohne Nummer hat keinen Adressaten.',
    });
  }

  if (spec.specVersion === PREVIOUS_SITE_SPEC_VERSION && (hasProject || hasLimit)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['specVersion'],
      message: `Eine Spezifikation der Version ${String(PREVIOUS_SITE_SPEC_VERSION)} kennt keine Quote. Wer beide Felder setzt, muss auf Version ${String(SITE_SPEC_VERSION)} gehen — sonst weicht der Fingerabdruck von dem ab, den der Master berechnet hat.`,
    });
  }
}

/**
 * Die Prüfungen rund um Einrichtungs-Dienste.
 *
 * Sie halten drei Zusagen aufrecht, auf die sich der Agent verlässt, statt sie
 * selbst noch einmal zu erschließen:
 *
 * 1. **Höchstens ein `init`-Dienst.** Zwei wären eine Reihenfolge, die niemand
 *    festgelegt hat — und die Einrichtung einer Anwendung ist nicht der Ort für
 *    eine zufällige.
 * 2. **`action` und `init` bedingen einander.** Eine Handlung ohne Rolle liefe
 *    dauerhaft mit; eine Rolle ohne Handlung wäre ein Container, der startet und
 *    nichts tut. Beides sind Fehler, die erst auf dem Node auffielen.
 * 3. **Ein `init`-Dienst bekommt keine Route.** Er läuft nur Sekunden; eine
 *    Traefik-Route auf ihn wäre ein Ziel, das gleich darauf verschwindet.
 */
interface ServiceRoleView {
  name: string;
  role?: ServiceRole;
  action?: unknown;
  http: unknown;
  dependsOn: readonly string[];
}

function checkInitServices(
  spec: { services: readonly ServiceRoleView[] },
  ctx: z.RefinementCtx,
): void {
  const initServices = spec.services.filter((service) => service.role === SERVICE_ROLES.init);
  const initNames = new Set(initServices.map((service) => service.name));

  if (initServices.length > 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['services'],
      message: `Höchstens ein Dienst darf die Rolle "init" tragen. Gefunden: ${String(initServices.length)} (${initServices.map((service) => service.name).join(', ')}). Zwei Einrichtungen brauchten eine Reihenfolge, die die Spezifikation nicht ausdrückt.`,
    });
  }

  for (const service of spec.services) {
    checkInitService(service, ctx);
    checkNoDependencyOnInit(service, initNames, ctx);
  }
}

/** Rolle und Handlung eines einzelnen Dienstes gegeneinander prüfen. */
function checkInitService(service: ServiceRoleView, ctx: z.RefinementCtx): void {
  const isInit = service.role === SERVICE_ROLES.init;
  const hasAction = service.action !== undefined;

  if (isInit && !hasAction) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['services'],
      message: `Der Dienst "${service.name}" trägt die Rolle "init", aber keine Handlung ("action"). Er startete und täte nichts.`,
    });
  }

  if (!isInit && hasAction) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['services'],
      message: `Der Dienst "${service.name}" trägt eine Handlung ("action"), aber nicht die Rolle "init". Eine Einrichtung, die dauerhaft mitliefe, würde bei jedem Neustart erneut ausgeführt.`,
    });
  }

  if (isInit && service.http !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['services'],
      message: `Der Dienst "${service.name}" trägt die Rolle "init" und zugleich "http". Ein Einrichtungs-Container beendet sich nach Sekunden; eine Route auf ihn zeigte ins Leere.`,
    });
  }
}

/**
 * Kein laufender Dienst darf auf einen Einrichtungs-Dienst warten.
 *
 * Der Agent nimmt die `init`-Dienste vor der Startreihenfolge heraus, weil sie
 * erst **nach** dem laufenden Stack an die Reihe kommen. Eine solche
 * Abhängigkeit stünde damit auf einem Dienst, den die Sortierung gar nicht mehr
 * kennt — und die Meldung spräche von einem unbekannten Dienst statt von einer
 * unerfüllbaren Reihenfolge.
 */
function checkNoDependencyOnInit(
  service: ServiceRoleView,
  initNames: ReadonlySet<string>,
  ctx: z.RefinementCtx,
): void {
  for (const dependency of service.dependsOn) {
    if (initNames.has(dependency)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['services'],
        message: `Der Dienst "${service.name}" hängt von "${dependency}" ab, das die Rolle "init" trägt. Ein Einrichtungs-Dienst läuft erst, wenn der Stack bereits steht — auf ihn kann nichts warten.`,
      });
    }
  }
}

/**
 * Die Datenbank-Prüfungen der Spezifikation.
 *
 * Ausgelagert, weil hier die tragende Sicherheitseigenschaft des Moduls steht
 * und sie zwischen den Dienst-Prüfungen untergehen würde.
 *
 * **Die Bindung an den Präfix ist die eigentliche Rechtetrennung.** Sie sagt:
 * Eine Spezifikation kann nur Datenbanken benennen, deren Name sich aus ihrer
 * eigenen Site-ID ableitet. Weil der Agent die Spezifikation vor dem Anwenden
 * erneut gegen dieses Schema parst, gilt die Regel auf beiden Seiten — und ein
 * fehlerhafter oder übernommener Master kann einer Site die Datenbank einer
 * anderen nicht unterschieben. Das scheitert an der Validierung, nicht an einer
 * Prüfung im Code, die jemand später umgeht.
 */
function checkDatabases(
  spec: { siteId: string; databases: readonly { name: string; user: string }[] },
  ctx: z.RefinementCtx,
): void {
  const names = new Set<string>();
  const users = new Set<string>();

  for (const database of spec.databases) {
    for (const [bezeichnung, wert] of [
      ['Datenbankname', database.name],
      ['Benutzername', database.user],
    ] as const) {
      if (!belongsToSite(spec.siteId, wert)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['databases'],
          message: `${bezeichnung} "${wert}" gehört nicht zu dieser Site. Erzeugte Namen tragen den aus der Site-ID abgeleiteten Präfix; ein anderer Name wäre der Zugriff auf die Daten eines fremden Kunden.`,
        });
      }
    }

    if (names.has(database.name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['databases'],
        message: `Der Datenbankname "${database.name}" kommt mehrfach vor.`,
      });
    }

    if (users.has(database.user)) {
      // Zwei Datenbanken mit demselben Benutzer hieße: ein Passwort für beide,
      // und die zweite Anweisung überschriebe still das Passwort der ersten.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['databases'],
        message: `Der Benutzername "${database.user}" kommt mehrfach vor. Jede Datenbank bekommt einen eigenen Benutzer.`,
      });
    }

    names.add(database.name);
    users.add(database.user);
  }
}

/**
 * Die SFTP-Prüfungen der Spezifikation — spiegelbildlich zu `checkDatabases`.
 *
 * Dieselbe tragende Eigenschaft, dieselbe Begründung: Eine Spezifikation kann
 * nur Konten benennen, deren Benutzername sich aus ihrer eigenen Site-ID
 * ableitet. Weil der Agent die Spezifikation vor dem Anwenden erneut gegen
 * dieses Schema parst, gilt die Regel auf beiden Seiten — und ein fehlerhafter
 * oder übernommener Master kann einer Site den Dateizugang einer anderen nicht
 * unterschieben.
 */
function checkSftpAccounts(
  spec: { siteId: string; sftpAccounts: readonly { username: string }[] },
  ctx: z.RefinementCtx,
): void {
  const usernames = new Set<string>();

  for (const account of spec.sftpAccounts) {
    if (!belongsToSite(spec.siteId, account.username)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sftpAccounts'],
        message: `Der SFTP-Benutzername "${account.username}" gehört nicht zu dieser Site. Erzeugte Namen tragen den aus der Site-ID abgeleiteten Präfix; ein anderer Name wäre der Zugriff auf die Dateien eines fremden Kunden.`,
      });
    }

    if (usernames.has(account.username)) {
      // Zwei Konten mit demselben Namen hieße: Das zweite überschriebe still
      // Passwort und Schlüssel des ersten — und welches gewinnt, hinge an der
      // Reihenfolge einer Datenbankabfrage.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sftpAccounts'],
        message: `Der SFTP-Benutzername "${account.username}" kommt mehrfach vor.`,
      });
    }

    usernames.add(account.username);
  }
}

export type SiteSpec = z.infer<typeof siteSpecSchema>;

// -- Spec-Hash ---------------------------------------------------------------

/**
 * Kanonische Textform einer Spezifikation.
 *
 * Objektschlüssel werden rekursiv sortiert, Array-Reihenfolgen bleiben erhalten
 * (bei Diensten und Einhängungen ist die Reihenfolge bedeutungstragend). Ohne
 * diese Normalisierung hinge der Hash davon ab, in welcher Reihenfolge ein
 * Renderer seine Felder zufällig schreibt — und eine harmlose Umstellung im
 * Code würde die gesamte Flotte neu anwenden lassen.
 */
export function canonicalizeSiteSpec(spec: SiteSpec): string {
  return canonicalJson(spec);
}

/** Rekursive Serialisierung mit sortierten Objektschlüsseln. */
function canonicalJson(value: unknown): string {
  if (value === undefined) {
    return 'null';
  }

  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((entry) => canonicalJson(entry)).join(',')}]`;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, entryValue]) => entryValue !== undefined)
    .sort(([left], [right]) => (left < right ? -1 : 1))
    .map(([key, entryValue]) => `${JSON.stringify(key)}:${canonicalJson(entryValue)}`);

  return `{${entries.join(',')}}`;
}
